import { useCallback, useMemo, useState } from "react";
import { Alert, Button, FileInput, Group, Select, Stack, Text, Textarea } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import customParseFormat from "dayjs/plugin/customParseFormat";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import {
  createAbsence,
  deleteAbsenceAttachment,
  downloadAbsenceAttachment,
  fetchMyDelegations,
  getMyAbsenceApplicationUrl,
  hasAbsenceApplication,
  submitAbsence,
  updateAbsence,
  uploadAbsenceAttachment,
  type Absence,
  type AbsenceType,
} from "../api/client";
import { useBadges } from "../hooks/BadgesContext";
import { ABSENCE_TYPE_OPTIONS, approxWorkingDays } from "./absenceMeta";
import { DelegateSuggestionModal } from "./DelegateSuggestionModal";

// US-66: лимит и whitelist должны совпадать с backend `_validate_attachment`
// в apps/api/views.py. Изменения тут — изменения там, иначе будет 400.
const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024; // 10 МБ
const ATTACHMENT_ACCEPT = ".pdf,.doc,.docx,.jpg,.jpeg,.png";

dayjs.extend(customParseFormat);

// US-92: парсер ручного ввода дат. Принимает «17.08.2026», «17.8.26»,
// «17,08,2026» и «17/08/2026» (разделители нормализуются в точки).
// Невалидный ввод → null (поле не заполняется).
export function parseRuDate(value: string): Date | null {
  const normalized = value.trim().replace(/[,\s/]+/g, ".");
  for (const fmt of ["DD.MM.YYYY", "D.M.YYYY", "D.M.YY"]) {
    const d = dayjs(normalized, fmt, true);
    if (d.isValid()) return d.toDate();
  }
  return null;
}

function formatSize(bytes: number | null | undefined): string {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

dayjs.locale("ru");

export interface AbsenceFormProps {
  /** Existing absence to edit. If absent — create mode. */
  initial?: Absence | null;
  onSaved: (absence: Absence) => void;
  onCancel?: () => void;
}

function toDate(value: string | null): Date | null {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d.toDate() : null;
}

function toIso(value: Date | null): string | null {
  if (!value) return null;
  return dayjs(value).format("YYYY-MM-DD");
}

export function AbsenceForm({ initial, onSaved, onCancel }: AbsenceFormProps) {
  const [absenceType, setAbsenceType] = useState<AbsenceType>(initial?.absence_type ?? "vacation");
  const [startDate, setStartDate] = useState<Date | null>(toDate(initial?.start_date ?? null));
  const [endDate, setEndDate] = useState<Date | null>(toDate(initial?.end_date ?? null));
  const [comment, setComment] = useState<string>(initial?.comment ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  // US-66: pendingFile — пользователь выбрал новый файл, но ещё не отправил
  // форму. Будет залит после save (или сразу, если у заявки уже есть pk).
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  // Локальная копия `initial.attachment_*` (обновляется после upload/delete).
  const [currentAttachmentName, setCurrentAttachmentName] = useState<string | null>(
    initial?.attachment_name ?? null,
  );
  const [currentAttachmentSize, setCurrentAttachmentSize] = useState<number | null>(
    initial?.attachment_size_bytes ?? null,
  );
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  // US-95 D4: подсказка-делегат после отправки отпуска (для ЛР/РП без делегата).
  const [delSuggest, setDelSuggest] = useState<{
    absence: Absence;
    showLr: boolean;
    showPm: boolean;
  } | null>(null);

  const workingDays = useMemo(() => approxWorkingDays(startDate, endDate), [startDate, endDate]);

  // Бланк заявления формируется прямо из формы (отпускные типы + валидные даты).
  // Открываем как обычную ссылку (component="a"), а не window.open — её не
  // придерживает блокировщик попапов.
  const appHref = useMemo(() => {
    if (!hasAbsenceApplication(absenceType) || !startDate || !endDate) return undefined;
    if (startDate > endDate) return undefined;
    return getMyAbsenceApplicationUrl({
      type: absenceType,
      start: toIso(startDate)!,
      end: toIso(endDate)!,
    });
  }, [absenceType, startDate, endDate]);

  const isEdit = !!initial;
  const isReturned = initial?.status === "returned";

  const validate = useCallback((): string | null => {
    if (!startDate) return "Укажите дату начала";
    if (!endDate) return "Укажите дату окончания";
    if (dayjs(endDate).isBefore(dayjs(startDate), "day")) {
      return "Дата окончания должна быть не раньше даты начала";
    }
    return null;
  }, [startDate, endDate]);

  const buildPayload = useCallback(() => {
    return {
      absence_type: absenceType,
      start_date: toIso(startDate)!,
      end_date: toIso(endDate)!,
      comment,
    };
  }, [absenceType, startDate, endDate, comment]);

  // US-59 #8: после успешного create/update/submit — refetch бейджей,
  // чтобы кружок в навигации обновился сразу, без задержки до 30s polling.
  const { refresh: refreshBadges } = useBadges();

  // US-66: загрузка файла на уже существующую заявку (или после её создания).
  const handleUploadFile = useCallback(
    async (absenceId: number, file: File): Promise<Absence | null> => {
      if (file.size > ATTACHMENT_MAX_BYTES) {
        setError(`Файл слишком большой: ${formatSize(file.size)}. ` + `Максимум 10 МБ.`);
        return null;
      }
      try {
        const updated = await uploadAbsenceAttachment(absenceId, file);
        setCurrentAttachmentName(updated.attachment_name ?? null);
        setCurrentAttachmentSize(updated.attachment_size_bytes ?? null);
        setPendingFile(null);
        return updated;
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Не удалось загрузить файл");
        return null;
      }
    },
    [],
  );

  const handleSave = useCallback(
    async (alsoSubmit: boolean) => {
      const v = validate();
      if (v) {
        setValidationError(v);
        return;
      }
      setValidationError(null);
      setError(null);
      setSaving(true);
      try {
        const payload = buildPayload();
        let saved: Absence;
        if (isEdit && initial) {
          saved = await updateAbsence(initial.id, payload);
        } else {
          saved = await createAbsence(payload);
        }
        // US-66: если пользователь выбрал файл в форме — заливаем его после
        // того как заявка создана/обновлена. Делается ДО submit, чтобы у ЛР
        // в момент получения письма уже был полный пакет.
        if (pendingFile) {
          const withFile = await handleUploadFile(saved.id, pendingFile);
          if (withFile) saved = withFile;
        }
        if (alsoSubmit) {
          saved = await submitAbsence(saved.id);
        }
        void refreshBadges();
        // US-95 D4: после отправки ОТПУСКА — если автор ЛР/РП и на этот период
        // ещё нет делегата, предложить назначить (модалка). Иначе — обычный onSaved.
        if (alsoSubmit && (absenceType === "vacation" || absenceType === "vacation_unpaid")) {
          try {
            const dels = await fetchMyDelegations();
            const showLr = !!dels.can_delegate?.lr && !dels.lr?.delegate;
            const showPm =
              !!dels.can_delegate?.pm && !(dels.pm?.projects ?? []).some((p) => p.delegate);
            if (showLr || showPm) {
              setDelSuggest({ absence: saved, showLr, showPm });
              return; // onSaved вызовется при закрытии модалки-подсказки
            }
          } catch {
            /* подсказку пропускаем — не мешаем основному флоу */
          }
        }
        onSaved(saved);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setSaving(false);
      }
    },
    [
      buildPayload,
      validate,
      isEdit,
      initial,
      onSaved,
      refreshBadges,
      pendingFile,
      handleUploadFile,
      absenceType,
    ],
  );

  // US-66: existing-file actions (для edit-режима).
  const handleDownloadExisting = useCallback(async () => {
    if (!initial?.id) return;
    setAttachmentBusy(true);
    try {
      await downloadAbsenceAttachment(initial.id, currentAttachmentName ?? "absence.pdf");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Не удалось скачать файл");
    } finally {
      setAttachmentBusy(false);
    }
  }, [initial?.id, currentAttachmentName]);

  const handleDeleteExisting = useCallback(async () => {
    if (!initial?.id) return;
    setAttachmentBusy(true);
    try {
      await deleteAbsenceAttachment(initial.id);
      setCurrentAttachmentName(null);
      setCurrentAttachmentSize(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Не удалось удалить файл");
    } finally {
      setAttachmentBusy(false);
    }
  }, [initial?.id]);

  return (
    <Stack gap="sm">
      {/* US-107: возврат от РП — до линейного руководителя заявка не дошла,
          и сотруднику важно понимать, что менять надо сроки под проект. */}
      {isReturned && initial?.pm_comment && (
        <Alert color="orange" title="Заявка возвращена на доработку (руководитель проекта)">
          {initial.pm_comment}
        </Alert>
      )}
      {isReturned && initial?.lr_comment && (
        <Alert color="orange" title="Заявка возвращена на доработку (ЛР)">
          {initial.lr_comment}
        </Alert>
      )}
      {isReturned && initial?.hr_comment && (
        <Alert color="orange" title="Заявка возвращена на доработку (HR)">
          {initial.hr_comment}
        </Alert>
      )}
      {error && (
        <Alert color="red" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {validationError && (
        <Alert color="red" withCloseButton onClose={() => setValidationError(null)}>
          {validationError}
        </Alert>
      )}

      <Select
        label="Тип отсутствия"
        data={ABSENCE_TYPE_OPTIONS}
        value={absenceType}
        onChange={(v) => v && setAbsenceType(v as AbsenceType)}
        allowDeselect={false}
      />

      {/* US-92: DateInput вместо DatePickerInput — дату можно ВВЕСТИ с
          клавиатуры («17.08.2026»), календарь остаётся как подсказка.
          position="top-start" — чтобы поповер не перекрывал кнопки
          сохранения внизу модалки. */}
      <Group grow align="flex-start">
        <DateInput
          label="Дата начала"
          placeholder="дд.мм.гггг"
          value={startDate}
          onChange={setStartDate}
          valueFormat="DD.MM.YYYY"
          locale="ru"
          dateParser={parseRuDate}
          popoverProps={{ position: "top-start", withinPortal: true }}
          aria-label="Дата начала"
        />
        <DateInput
          label="Дата окончания"
          placeholder="дд.мм.гггг"
          value={endDate}
          onChange={setEndDate}
          valueFormat="DD.MM.YYYY"
          locale="ru"
          dateParser={parseRuDate}
          minDate={startDate ?? undefined}
          popoverProps={{ position: "top-start", withinPortal: true }}
          aria-label="Дата окончания"
        />
      </Group>

      <Textarea
        label="Комментарий"
        placeholder="Необязательный комментарий"
        autosize
        minRows={2}
        maxRows={6}
        value={comment}
        onChange={(e) => setComment(e.currentTarget.value)}
      />

      {/* US-66: файл-заявление. Виден всегда (можно прикладывать и при создании
          черновика, и при редактировании уже отправленной/возвращённой заявки).
          Существующий файл показывается с кнопками «Скачать» и «Удалить». */}
      <Stack gap={4}>
        {currentAttachmentName ? (
          <Group gap="xs" wrap="nowrap">
            <Text size="sm" data-testid="absence-attachment-current">
              📎 <b>{currentAttachmentName}</b>{" "}
              {currentAttachmentSize !== null && (
                <Text component="span" size="xs" c="dimmed">
                  ({formatSize(currentAttachmentSize)})
                </Text>
              )}
            </Text>
            <Button
              variant="subtle"
              size="compact-xs"
              onClick={handleDownloadExisting}
              loading={attachmentBusy}
              disabled={attachmentBusy || saving}
            >
              Скачать
            </Button>
            <Button
              variant="subtle"
              color="red"
              size="compact-xs"
              onClick={handleDeleteExisting}
              disabled={attachmentBusy || saving}
            >
              Удалить
            </Button>
          </Group>
        ) : null}
        {hasAbsenceApplication(absenceType) && (
          <Stack gap={4}>
            <Button
              component="a"
              href={appHref}
              download
              variant="light"
              color="teal"
              disabled={!appHref}
              data-testid="absence-application-generate"
              style={{ alignSelf: "flex-start" }}
            >
              Скачать предзаполненный шаблон заявления (Word)
            </Button>
            <Text size="xs" c="dimmed">
              Приложите к заявлению скан-копию подписанного заявления.
            </Text>
          </Stack>
        )}
        <FileInput
          label={currentAttachmentName ? "Заменить файл" : "Файл заявления"}
          placeholder="Выберите PDF / DOCX / изображение"
          accept={ATTACHMENT_ACCEPT}
          value={pendingFile}
          onChange={setPendingFile}
          description="PDF / DOC / DOCX / JPG / PNG, до 10 МБ. Файл будет загружен при сохранении."
          clearable
          disabled={saving}
          data-testid="absence-attachment-input"
        />
      </Stack>

      <Text size="sm" c="dimmed">
        Рабочих дней (предв.): <b>{workingDays}</b>
      </Text>

      <Group justify="flex-end" gap="xs">
        {onCancel && (
          <Button variant="default" onClick={onCancel} disabled={saving}>
            Отмена
          </Button>
        )}
        <Button
          variant="light"
          onClick={() => handleSave(false)}
          loading={saving}
          disabled={saving}
        >
          Сохранить как черновик
        </Button>
        <Button onClick={() => handleSave(true)} loading={saving} disabled={saving}>
          Сохранить и отправить
        </Button>
      </Group>

      <DelegateSuggestionModal
        opened={!!delSuggest}
        start={delSuggest?.absence.start_date ?? null}
        end={delSuggest?.absence.end_date ?? null}
        showLr={!!delSuggest?.showLr}
        showPm={!!delSuggest?.showPm}
        onClose={() => {
          const a = delSuggest?.absence;
          setDelSuggest(null);
          if (a) onSaved(a);
        }}
      />
    </Stack>
  );
}
